// src/pages/Profile.tsx
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { useNavigate, useParams } from "react-router-dom";
import { apiGet, apiPost } from "../api/client";
import { companyRoute } from "../utils/routeHelpers";
import type { AuthMe } from "../types/auth";
import ProBadge from "../components/ProBadge";
import { Button } from "../components/Button";
import { A } from "../styles/appleClasses";

type ProfileError =
  | { kind: "local"; code: "failed" | "emailInUse" | "inactiveUser" | "staffProRequired" }
  | { kind: "raw"; value: React.ReactNode };

function getErrorOutcome(err: any): ProfileError {
  const detail = err?.response?.data?.detail;
  const status = err?.response?.status;
  if (status === 400 && detail === "Email already in use") return { kind: "local", code: "emailInUse" };
  if (status === 403 && detail === "Inactive user.") return { kind: "local", code: "inactiveUser" };
  if (status === 403 && detail === "Staff access requires an active Pro subscription.") return { kind: "local", code: "staffProRequired" };
  return detail ? { kind: "raw", value: detail } : { kind: "local", code: "failed" };
}

type Props = {
  mode?: "page" | "inlay";
  onClose?: () => void;
  initialUser?: AuthMe | null;
  onUpdated?: (patch: { full_name?: string; email?: string }) => void;
};

export default function Profile({
  mode = "page",
  onClose,
  initialUser = null,
  onUpdated,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();

  const [me, setMe] = useState<AuthMe | null>(initialUser);
  const [name, setName] = useState(initialUser?.full_name || "");
  const [email, setEmail] = useState(initialUser?.email || "");

  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(false);
  const [error, setError] = useState<ProfileError | null>(null);
  const errorText = !error ? null : error.kind === "raw" ? error.value :
    error.code === "emailInUse" ? t("profile.emailInUse") :
    error.code === "inactiveUser" ? t("accountInlays.errors.inactiveUser") :
    error.code === "staffProRequired" ? t("accountInlays.errors.staffProRequired") :
    t("profile.updateFailed");

  const avatarUrl = useMemo(() => {
    const u = me ?? initialUser;
    if (!u) return "";
    const style = u.avatar_style || "adventurer";
    const seed = u.avatar_seed || u.full_name || "Ricebook";
    return `https://api.dicebear.com/7.x/${style}/svg?seed=${encodeURIComponent(
      seed
    )}`;
  }, [me, initialUser]);

  // Load user if not provided (or sync state if provided)
  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (initialUser) {
        if (cancelled) return;
        setMe(initialUser);
        setName(initialUser.full_name || "");
        setEmail(initialUser.email || "");
        return;
      }

      try {
        const u = await apiGet<AuthMe>("/auth/me");
        if (cancelled) return;
        setMe(u);
        setName(u.full_name || "");
        setEmail(u.email || "");
      } catch {
        // ignore
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [initialUser]);

  const handleClose = () => {
    if (mode === "inlay") {
      onClose?.();
      return;
    }
    navigate(companyRoute(company_id, "/"));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;

    setSaving(true);
    setMsg(false);
    setError(null);

    try {
      const payload = { full_name: name.trim(), email: email.trim() };
      await apiPost("/auth/update-profile", payload);

      setMsg(true);
      onUpdated?.(payload);

      window.setTimeout(() => {
        if (mode === "inlay") onClose?.();
        else navigate(companyRoute(company_id, "/"));
      }, 700);
    } catch (err: any) {
      setError(getErrorOutcome(err));
    } finally {
      setSaving(false);
    }
  };

  const labelCls = `block mb-1 ${A.label}`;
  const inputCls = A.input;

  const content = (
    <>
      {/* Avatar + pro badge */}
      <div className="flex flex-col items-center mb-6">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={t("profile.avatar")}
            className="w-20 h-20 rounded-full border-2 border-appleblue object-cover"
          />
        ) : (
          <div className="w-20 h-20 rounded-full bg-gray-100 border-2 border-gray-200" />
        )}
        <div className="mt-2">
          <ProBadge show={!!me?.is_user_pro} size="xs" />
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-4">
        <div>
          <label className={labelCls}>{t("register.name")}</label>
          <input
            className={inputCls}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            placeholder={t("register.name")}
          />
        </div>

        <div>
          <label className={labelCls}>{t("directory.common.email")}</label>
          <input
            className={inputCls}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            placeholder={t("directory.common.email")}
          />
          <div className={`mt-1 ${A.label}`}>
            {t("profile.emailVerificationHint")}
          </div>
        </div>

        <Button className="w-full" type="submit" variant="primary" disabled={saving}>
          {saving ? t("directory.common.savingDots") : t("directory.common.save")}
        </Button>

        {/* reserve space to avoid layout jump */}
        <div style={{ minHeight: 24 }}>
          {msg && <div className="text-green-600 text-[13px]">{t("profile.updated")}</div>}
          {error && <div className="text-red-500 text-[13px]">{errorText}</div>}
        </div>
      </form>
    </>
  );

  // Inlay modal wrapper (centered header)
  if (mode === "inlay") {
    return (
      <div
        className={`fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4 ${A.body}`}
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) handleClose();
        }}
      >
        <div className={`w-full max-w-md overflow-hidden ${A.modal}`}>
          <div className="grid grid-cols-[40px_1fr_40px] items-center px-5 py-4 border-b border-gray-100">
            <div />
            <h3 className={`${A.title} text-center`}>{t("accountMenu.profile")}</h3>
            <button
              onClick={handleClose}
              className={`${A.iconBtn} justify-self-end p-1 text-gray-500 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
              aria-label={t("directory.common.close")}
              type="button"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="px-6 py-6">{content}</div>
        </div>
      </div>
    );
  }

  // Page mode (Apple standard wrapper)
  return (
    <div className={`min-h-screen bg-gray-50 ${A.body} px-3 py-10`}>
      <div className="max-w-md mx-auto">
        <div className={`${A.card} p-6 sm:p-8 relative`}>
          <button
            onClick={handleClose}
            className={`${A.iconBtn} absolute top-3 right-3 p-2 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
            aria-label={t("directory.common.close")}
            type="button"
          >
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </button>

          <div className="text-center mb-6">
            <h2 className={A.title}>{t("accountMenu.profile")}</h2>
          </div>

          {content}
        </div>
      </div>
    </div>
  );
}
